import { Trans, useTranslation } from 'next-i18next'

import {
  Heading3,
  LinkItem,
  LinkList,
  LinkText,
  Paragraph,
  Section3
} from '@/features/Listings/seo/elements'
import AverageRentFaq from '@/features/Listings/seo/search-results-content/content/average-rent-faq'
import ContentTemplate from '@/features/Listings/seo/search-results-content/content/city/content-template'
import getFilterLinks from '@/features/Listings/seo/search-results-content/content/get-filter-links'

import Amenities from '../amenities'
import RentingIn from '../renting-in'

const Whistler = ({
  filterSlug,
  isCanadaPage,
  cityName,
  cityNameWithState,
  cityData,
  isChinese
}) => {
  const { t } = useTranslation()
  const { state, slug: citySlug } = cityData
  const links = getFilterLinks(isCanadaPage, citySlug)

  const bcNeighbourhoodsLink = isChinese
    ? 'https://liv.rent/blog/zh/%e8%a1%97%e5%8c%ba%e6%8c%87%e5%8d%97/%e5%8d%91%e8%af%97%e7%9c%81-%e8%a1%97%e5%8c%ba%e6%8c%87%e5%8d%97/'
    : 'https://liv.rent/blog/neighbourhoods/british-columbia-neighbourhoods/'

  const getAbout = () => {
    return (
      <>
        <Paragraph>
          <Trans i18nKey="seo.city.whistler:About Whistler Paragraph 1">
            Whistler, located in the picturesque province of{' '}
            <LinkText href="/rental-listings/british-columbia">
              British Columbia (BC)
            </LinkText>
            , stands as a captivating haven for outdoor enthusiasts and those
            seeking natural beauty. Nestled in the Coast Mountains, this vibrant
            town boasts exceptional skiing, snowboarding, and year-round
            activities like hiking and mountain biking. The stunning scenery,
            including serene lakes and lush forests, serves as a breathtaking
            backdrop to everyday life.
          </Trans>
        </Paragraph>
        <Paragraph>
          <Trans i18nKey="seo.city.whistler:About Whistler Paragraph 2">
            At the heart of the community lies Whistler Village, a
            pedestrian-friendly hub featuring shops, restaurants, and cultural
            events, fostering a vibrant and welcoming atmosphere. Despite its
            global appeal, Whistler maintains a tight-knit sense of community,
            supported by a range of cultural events and festivals. This popular
            tourist destination not only offers stunning vistas but also
            provides various job opportunities, particularly in the hospitality
            and tourism sectors. Discover the allure of Whistler, a gem among
            the many{' '}
            <LinkText href={bcNeighbourhoodsLink}>
              cities and neighbourhoods in BC
            </LinkText>
            .
          </Trans>
        </Paragraph>
      </>
    )
  }

  const getRentingIn = () => {
    return (
      <RentingIn
        filterSlug={filterSlug}
        cityName={cityName}
        rentingTipsLink={bcNeighbourhoodsLink}
      />
    )
  }

  const getAverageRent = () => {
    return (
      <AverageRentFaq
        filterSlug={filterSlug}
        isCanadaPage={isCanadaPage}
        cityName={cityName}
        cityNameWithState={cityNameWithState}
        citySlug={citySlug}
        rentAmounts={{
          bed1: '$1,800 - $2,500',
          bed2: '$2,300 - $3,200',
          bed3: '$3,000 - $4,000'
        }}
        rentReportCity={t('cities:city_name_vancouver')}
        rentReportLink="https://liv.rent/blog/rent-reports/vancouver/"
        rentReportLinkZh="https://liv.rent/blog/zh/%E7%A7%9F%E9%87%91%E7%BB%9F%E8%AE%A1/%E6%B8%A9%E5%93%A5%E5%8D%8E/"
      />
    )
  }

  const getApartmentHunting = () => {
    const neighborhoodsLink = isChinese
      ? 'https://liv.rent/blog/zh/%E8%A1%97%E5%8C%BA%E6%8C%87%E5%8D%97/'
      : 'https://liv.rent/blog/neighbourhoods/'

    return (
      <>
        <Paragraph>
          <Trans i18nKey="seo.city.whistler:Apartment Hunting in Whistler Paragraph">
            Finding affordable rentals in Whistler can be challenging due to its
            popularity as a world-renowned ski destination. While there might be
            some lower-priced options, finding{' '}
            <LinkText href={links.apartmentsUnder1000}>
              apartments for rent under $1000 in Whistler
            </LinkText>{' '}
            is rare. To navigate the local rental market, consider referring to
            <LinkText href={neighborhoodsLink}>
              guides on neighbourhoods in Canada
            </LinkText>
            .
          </Trans>
        </Paragraph>
        <Paragraph>
          <Trans i18nKey="seo:Apartment Hunting in LOCATION Paragraph 2">
            In addition to choosing your location, it’s important to know what
            rental types are available in {{ cityName }} (e.g.{' '}
            <LinkText href={links.bed1}>1 bedroom apartments</LinkText>,{' '}
            <LinkText href={links.bed2}>2 bedroom apartments</LinkText>,{' '}
            <LinkText href={links.bed3}>3 bedroom apartments</LinkText>,{' '}
            <LinkText href={links.studio}>studio apartments</LinkText>) and the
            average rent for each property type. Refer below to figure out your
            options - ranging from{' '}
            <LinkText href={links.furnished}>furnished rentals</LinkText> and{' '}
            <LinkText href={links.unfurnished}>unfurnished rentals</LinkText> to{' '}
            <LinkText href={links.longTerm}>long term rentals</LinkText> and{' '}
            <LinkText href={links.shortTerm}>short term rentals</LinkText>.
          </Trans>
        </Paragraph>
      </>
    )
  }

  const getRentalTypes = () => {
    return (
      <>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Apartments for rent LOCATION"
              values={{
                location: cityName
              }}
              defaults="Apartments for rent {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo.city.whistler:Apartments for rent Whistler Paragraph">
              In Whistler,{' '}
              <LinkText href={links.apartment}>apartments for rent</LinkText>{' '}
              are not as numerous as condos. Additionally, these apartments are
              more catered towards a mix of long-term residents and seasonal
              workers.
            </Trans>
          </Paragraph>
        </Section3>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Houses for rent LOCATION"
              values={{
                location: cityName
              }}
              defaults="Houses for rent {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo.city.whistler:Houses for rent Whistler Paragraph">
              While there are single-family detached houses in Whistler, they
              tend to be less dominant than condos and townhouses. Many of these
              houses are luxurious, catering to the upscale market or serving as
              vacation homes. As a result, you should expect a higher price tag
              for{' '}
              <LinkText href={links.house}>
                houses for rent in Whistler
              </LinkText>
              .
            </Trans>
          </Paragraph>
        </Section3>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Townhouses for rent LOCATION"
              values={{
                location: cityName
              }}
              defaults="Townhouses for rent {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo:Townhouses for rent LOCATION Paragraph">
              Townhouses are another popular form of housing in {{ cityName }}{' '}
              that combine the best qualities of both apartments and whole
              houses. Typically less expensive than houses,{' '}
              <LinkText href={links.townhouse}>townhouses for rent</LinkText>{' '}
              offer an attractive combination of freedom and space, generally
              coming with your own private yard and entrance.
            </Trans>
          </Paragraph>
        </Section3>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Basements for rent LOCATION"
              values={{
                location: cityName
              }}
              defaults="Basements for rent {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo:Basements for rent CITY Paragraph">
              For those looking to find affordable rental housing in this
              increasingly expensive city, basements for rent in {{ cityName }}{' '}
              pose an attractive alternative. Since house prices are so
              expensive, homeowners will sometimes rent out their basements as a
              way to offset their mortgage payments. This makes it easy for
              renters to find relatively inexpensive basements for rent in all
              areas of the city.
            </Trans>
          </Paragraph>
        </Section3>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Condos for rent LOCATION"
              values={{
                location: cityName
              }}
              defaults="Condos for rent {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo.city.whistler:Condos for rent Whistler Paragraph">
              Given Whistler’s status as a premier tourist destination,{' '}
              <LinkText href={links.apartment}>condos for rent</LinkText>,
              especially those near the ski slopes, are quite common. They cater
              to both short-term vacation rentals and long-term residents.
            </Trans>
          </Paragraph>
        </Section3>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Rooms for rent LOCATION"
              values={{
                location: cityName
              }}
              defaults="Rooms for rent {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo.city.whistler:Rooms for rent Whistler Paragraph">
              <LinkText href={links.room}>Rooms for rent in Whistler</LinkText>,
              especially in shared housing setups, are relatively common,
              particularly among seasonal workers, ski instructors, and others
              who come to the town for short-term stays.
            </Trans>
          </Paragraph>
        </Section3>
      </>
    )
  }

  const getSpecialTypes = () => {
    return (
      <>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Pet friendly rentals in LOCATION"
              values={{
                location: cityName
              }}
              defaults="Pet friendly rentals in {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo:Pet friendly rentals CITY Paragraph">
              Though not every landlord is keen on allowing pets, there are a
              good number of{' '}
              <LinkText href={links.petFriendly}>
                pet-friendly rentals in {{ cityName }}
              </LinkText>{' '}
              for those with furry friends. Both{' '}
              <LinkText href={links.dogFriendly}>dog-friendly</LinkText> and{' '}
              <LinkText href={links.catFriendly}>cat-friendly</LinkText> rental
              units are available, as well as properties where both are allowed.
              Keep in mind that some {{ cityName }} neighbourhoods are better
              suited to those with pets, and adjust your search accordingly.
            </Trans>
          </Paragraph>
        </Section3>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Furnished rentals in LOCATION"
              values={{
                location: cityName
              }}
              defaults="Furnished rentals in {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo.city.whistler:Furnished rentals Whistler Paragraph">
              As a popular tourist destination, there’s a notable presence of{' '}
              <LinkText href={links.furnished}>
                furnished rentals in Whistler
              </LinkText>{' '}
              to cater to both short-term and new residents. Renters seeking
              furnished units should search for these units using filters, but
              do be prepared to pay more in rent. There are{' '}
              <LinkText href="https://liv.rent/blog/renters/renting-furnished-unfurnished/">
                pros and cons to renting furnished units compared to unfurnished
                units
              </LinkText>
              , so deciding whether it’s worth it to buy furniture when moving
              is a good first step.
            </Trans>
          </Paragraph>
        </Section3>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Short term rentals in LOCATION"
              values={{
                location: cityName
              }}
              defaults="Short term rentals in {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo.city.whistler:Short term rentals Whistler Paragraph">
              Whistler, renowned as a global skiing and mountain biking
              destination, boasts a vibrant market for{' '}
              <LinkText href={links.shortTerm}>short-term rentals</LinkText>.
              Catering primarily to tourists, these rentals range from luxurious
              chalets with mountain views to cozy condos steps away from the ski
              lifts. Many of these properties come fully furnished, offering
              guests the comforts of home alongside the convenience of being
              near Whistler’s prime attractions. For visitors, these short-term
              rentals provide a flexible lodging option, allowing them to
              immerse themselves in Whistler’s community and landscape, even if
              just for a few days.
            </Trans>
          </Paragraph>
        </Section3>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Cheap rentals in LOCATION"
              values={{
                location: cityName
              }}
              defaults="Cheap rentals in {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo.city.whistler:Cheap rentals Whistler Paragraph">
              Finding{' '}
              <LinkText href={links.under1000}>
                cheap rentals in Whistler
              </LinkText>{' '}
              can be a challenging endeavor, given the town’s status as a
              premier ski and tourist destination. However, for those on a
              tighter budget, seeking{' '}
              <LinkText href={links.room}>rooms for rent</LinkText> within
              shared housing can be a more affordable option. By opting for this
              arrangement, individuals can secure a place in Whistler without
              bearing the full brunt of the town’s premium rental prices.
              Sharing spaces with locals or fellow seasonal workers also offers
              an opportunity for camaraderie and gaining insights into the best
              local spots and experiences.
            </Trans>
          </Paragraph>
        </Section3>
      </>
    )
  }

  const getLiving = () => {
    return (
      <>
        <Paragraph>
          <Trans i18nKey="seo.city.whistler:Living in Whistler Paragraph">
            Living in Whistler offers an unparalleled blend of outdoor adventure
            and small-town charm. As a world-class ski destination, residents
            have direct access to the slopes in the winter, while the summer
            months reveal a myriad of activities like mountain biking, hiking,
            and golfing. Beyond its natural attractions, Whistler Village boasts
            a lively atmosphere with an array of restaurants, shops, and
            cultural events. While the cost of living can be high due to its
            global reputation and tourist influx, the town provides a mix of
            rental and homeownership opportunities.
          </Trans>
        </Paragraph>
        {isChinese ? (
          <LinkList>
            <LinkItem href="https://liv.rent/blog/zh/%e7%a7%9f%e5%ae%a2/%e5%8a%a0%e6%8b%bf%e5%a4%a7%e7%95%99%e5%ad%a65%e5%a4%a7%e5%9f%8e%e5%b8%82-%e7%a7%9f%e6%88%bf%e3%80%81%e5%ad%a6%e8%b4%b9%e3%80%81%e5%b7%a5%e4%bd%9c%e3%80%81%e7%ad%be%e8%af%81%e8%af%a6%e8%a7%a3/">
              加拿大最佳移民城市
            </LinkItem>
            <LinkItem href="https://liv.rent/blog/zh/%e7%a7%9f%e5%ae%a2/%e6%b8%a9%e5%93%a5%e5%8d%8e%e3%80%81%e5%a4%9a%e4%bc%a6%e5%a4%9a%e6%af%94%e8%be%83/">
              温哥华 vs 多伦多：生活成本比较
            </LinkItem>
            <LinkItem href="https://liv.rent/blog/zh/%E8%A1%97%E5%8C%BA%E6%8C%87%E5%8D%97/%E6%B8%A9%E5%93%A5%E5%8D%8E%E5%92%8C%E7%B4%A0%E9%87%8C%E5%B1%85%E4%BD%8F%E6%88%90%E6%9C%AC%E6%AF%94%E8%BE%83/">
              温哥华 vs 素里：生活成本比较
            </LinkItem>
            <LinkItem href="https://liv.rent/blog/zh/toronto-zh/2021%e7%89%88%e5%a4%9a%e4%bc%a6%e5%a4%9a%e3%80%81%e8%92%99%e7%89%b9%e5%88%a9%e5%b0%94%e7%94%9f%e6%b4%bb%e6%88%90%e6%9c%ac%e6%af%94%e8%be%83/">
              多伦多 vs 蒙特利尔：生活成本比较
            </LinkItem>
          </LinkList>
        ) : (
          <LinkList>
            <LinkItem href="https://liv.rent/blog/2020/11/best-canadian-cities-for-immigration/">
              Best Canadian cities for immigration
            </LinkItem>
            <LinkItem href="https://liv.rent/blog/2019/11/vancouver-or-toronto-a-cost-of-living-comparison/">
              Vancouver vs Toronto: Cost of living comparison
            </LinkItem>
            <LinkItem href="https://liv.rent/blog/2021/02/surrey-vs-vancouver-bc-comparison/">
              Vancouver vs Surrey: Cost of living comparison
            </LinkItem>
            <LinkItem href="https://liv.rent/blog/2021/04/toronto-vs-montreal-cost-of-living-comparison/">
              Toronto vs Montreal: Cost of living comparison
            </LinkItem>
          </LinkList>
        )}
      </>
    )
  }

  const getPopularHoods = () => {
    return (
      <Paragraph>
        <Trans i18nKey="seo.city.whistler:Popular Neighbourhoods Whistler Paragraph">
          Like any city, Whistler is made up of smaller neighbourhoods with
          their own distinct features. Some of the most popular neighbourhoods
          include Whistler Village, Upper Village, Creekside, Function Junction,
          Alpine Meadows, Nesters, Whistler Cay Heights, and Emerald Estates.
          For more info about Whistler, you can consult this guide on{' '}
          <LinkText href={bcNeighbourhoodsLink}>BC neighbourhoods</LinkText>.
        </Trans>
      </Paragraph>
    )
  }

  const getLandmarks = () => {
    return (
      <Paragraph>
        <Trans i18nKey="seo.city.whistler:Local Landmarks Whistler Paragraph">
          Whistler, nestled in the Coast Mountains of British Columbia, is
          brimming with iconic landmarks that draw visitors year-round. The
          Whistler and Blackcomb Mountains are perhaps the most recognized,
          offering world-renowned ski slopes and breathtaking alpine views. The
          Peak 2 Peak Gondola, holding the world record for the longest free
          span between towers, provides panoramic vistas as it connects these
          two mountains. Lost Lake, a serene spot tucked away, becomes a popular
          swimming destination in the summer and a cross-country skiing haven in
          the winter. Additionally, the Squamish Lil’wat Cultural Centre stands
          as a testament to the region’s indigenous heritage, offering insights
          into the traditions of the Squamish and Lil’wat Nations.
        </Trans>
      </Paragraph>
    )
  }

  const content = {
    about: getAbout(),
    rentingIn: getRentingIn(),
    averageRent: getAverageRent(),
    apartmentHunting: getApartmentHunting(),
    rentalTypes: getRentalTypes(),
    specialTypes: getSpecialTypes(),
    amenities: (
      <Amenities cityName={cityName} filterSlug={filterSlug} links={links} />
    ),
    living: getLiving(),
    popularHoods: getPopularHoods(),
    landmarks: getLandmarks()
  }

  return (
    <ContentTemplate
      content={content}
      filterSlug={filterSlug}
      isCanadaPage={isCanadaPage}
      cityName={cityName}
      cityNameWithState={cityNameWithState}
      citySlug={citySlug}
      state={state}
    />
  )
}

export default Whistler
